import { AgentProtocol, AgentOutput } from './types';
import { TechnicalAgent } from './TechnicalAgent';
import { ComplianceAgent } from './ComplianceAgent';
import { PricingAgent } from './PricingAgent';
import { Contractor } from '../../types';

export class AgentOrchestrator {
    private agents: Record<string, AgentProtocol>;

    constructor() {
        this.agents = {
            'Technical Analyst': new TechnicalAgent(),
            'Compliance Officer': new ComplianceAgent(),
            'Pricing Strategist': new PricingAgent()
        };
    }

    /**
     * Orchestrates a multi-agent analysis of a government opportunity.
     * 
     * LOGIC FLOW (For Future Engineers):
     * 1. Extracts technical 'userStack' from the Contractor profile (NAICS/Keywords).
     * 2. Runs internal agents in parallel to minimize latency.
     * 3. Synthesizes individual agent outputs into a unified executive summary.
     */
    async orchestrate(params: {
        opportunity: any;
        userProfile: Partial<Contractor>;
        files: any[];
    }): Promise<{
        summary: string;
        agentOutputs: Record<string, AgentOutput>;
        finalRecommendation: string;
    }> {
        const { opportunity, userProfile } = params;

        // 1. Prepare context for agents
        const contractorName = userProfile.name || 'Our Firm';
        const userStack = userProfile.keywords?.join(', ') || 'General IT and Professional Services';

        console.log(`[Orchestrator] Starting orchestration for ${contractorName} on ${opportunity.title}`);

        // 2. Run agents in parallel
        const agentPromises = Object.entries(this.agents).map(async ([role, agent]) => {
            try {
                const output = await agent.process({
                    opportunity,
                    userStack,
                    contractorName
                });
                return { role, output };
            } catch (err) {
                console.error(`Agent [${role}] failed:`, err);
                return {
                    role,
                    output: {
                        role,
                        summary: "Analysis failed due to model error.",
                        score: 0,
                        findings: ["Error during neural synthesis."],
                        recommendation: "Retry requested.",
                        confidence: 0,
                        reasoning: "Agent crashed likely due to context limits or API error."
                    }
                };
            }
        });

        const results = await Promise.all(agentPromises);

        const agentOutputs = results.reduce((acc, curr) => {
            acc[curr.role] = curr.output;
            return acc;
        }, {} as Record<string, AgentOutput>);

        // 3. Synthesis Logic
        const technicalRec = agentOutputs['Technical Analyst']?.recommendation || '';
        const complianceRec = agentOutputs['Compliance Officer']?.recommendation || '';

        let finalRec = 'REVIEW';

        if (technicalRec.includes('GO') && complianceRec.includes('GO')) {
            finalRec = 'GO';
        } else if (complianceRec.includes('NO-GO')) {
            finalRec = 'NO-GO';
        }

        const summary = `
### 🤖 Multi-Agent Synthesis
**Technical**: ${agentOutputs['Technical Analyst']?.reasoning || 'No technical analysis available.'}
**Compliance**: ${agentOutputs['Compliance Officer']?.reasoning || 'No compliance analysis available.'}
**Pricing**: ${agentOutputs['Pricing Strategist']?.reasoning || 'No pricing strategy generated.'}
`;

        return {
            summary,
            agentOutputs,
            finalRecommendation: finalRec
        };
    }
}
